/**
 * Subtitle Settings Panel Component
 * Combined admin panel for STT (subtitle) and Translation settings
 *
 * Logic:
 * - STT OFF → Translation also OFF
 * - STT ON → Translation also ON
 * - Translation OFF → STT unchanged
 */

import { useState, useEffect } from 'react';
import {
  apiClient,
  STTProvider,
  STTSettingsResponse,
  TranslationProvider,
  TranslationSettingsResponse,
} from '../lib/api-client';

interface SubtitleSettingsPanelProps {
  className?: string;
}

const STT_PROVIDER_LABELS: Record<STTProvider, string> = {
  deepgram: 'Deepgram',
  gladia: 'Gladia',
};

const STT_PROVIDER_DESCRIPTIONS: Record<STTProvider, string> = {
  deepgram: 'リアルタイム音声認識に特化',
  gladia: 'マルチ言語対応の音声認識',
};

const TRANSLATION_PROVIDER_LABELS: Record<TranslationProvider, string> = {
  deepl: 'DeepL',
  openai: 'OpenAI',
};

const TRANSLATION_PROVIDER_DESCRIPTIONS: Record<TranslationProvider, string> = {
  deepl: '高精度な機械翻訳（50万文字/月無料）',
  openai: 'GPT-4o-miniによる自然な翻訳',
};

// Compact Toggle Switch Component
function ToggleSwitch({
  checked,
  onChange,
  disabled = false,
  size = 'normal',
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'normal' | 'small';
}) {
  const sizeClasses = size === 'small'
    ? 'w-9 h-5 after:h-4 after:w-4'
    : 'w-11 h-6 after:h-5 after:w-5';

  return (
    <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className={`${sizeClasses} bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:transition-all peer-checked:bg-pink-500 peer-disabled:opacity-50`}></div>
    </label>
  );
}

export default function SubtitleSettingsPanel({ className = '' }: SubtitleSettingsPanelProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // STT settings
  const [sttSettings, setSttSettings] = useState<STTSettingsResponse | null>(null);
  const [selectedSTTProvider, setSelectedSTTProvider] = useState<STTProvider>('deepgram');
  const [isSTTEnabled, setIsSTTEnabled] = useState(false);

  // Translation settings
  const [translationSettings, setTranslationSettings] = useState<TranslationSettingsResponse | null>(null);
  const [selectedTranslationProvider, setSelectedTranslationProvider] = useState<TranslationProvider>('deepl');
  const [isTranslationEnabled, setIsTranslationEnabled] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [sttData, translationData] = await Promise.all([
        apiClient.getSTTSettings(),
        apiClient.getTranslationSettings(),
      ]);

      setSttSettings(sttData);
      setSelectedSTTProvider(sttData.provider);
      setIsSTTEnabled(sttData.enabled);

      setTranslationSettings(translationData);
      setSelectedTranslationProvider(translationData.provider);
      setIsTranslationEnabled(sttData.enabled && translationData.enabled);
    } catch (err) {
      setError('設定の読み込みに失敗しました');
      console.error('Failed to load settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSTTToggle = (enabled: boolean) => {
    setIsSTTEnabled(enabled);
    if (!enabled) {
      setIsTranslationEnabled(false);
    } else {
      setIsTranslationEnabled(true);
    }
  };

  const handleTranslationToggle = (enabled: boolean) => {
    if (enabled && !isSTTEnabled) return;
    setIsTranslationEnabled(enabled);
  };

  const handleSTTProviderToggle = (provider: STTProvider, enabled: boolean) => {
    if (enabled) setSelectedSTTProvider(provider);
  };

  const handleTranslationProviderToggle = (provider: TranslationProvider, enabled: boolean) => {
    if (enabled) setSelectedTranslationProvider(provider);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const [updatedSTT, updatedTranslation] = await Promise.all([
        apiClient.updateSTTSettings({
          provider: selectedSTTProvider,
          enabled: isSTTEnabled,
        }),
        apiClient.updateTranslationSettings({
          provider: selectedTranslationProvider,
          enabled: isTranslationEnabled,
        }),
      ]);

      setSttSettings(updatedSTT);
      setTranslationSettings(updatedTranslation);
      setShowSaveModal(true);
    } catch (err) {
      setError('設定の保存に失敗しました');
      console.error('Failed to save settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className={`text-center text-gray-600 py-4 ${className}`}>読み込み中...</div>;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* STT Section */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-gray-800">字幕・音声認識 (STT)</h3>

        {/* STT Toggle + Provider in grid layout */}
        <div className="grid grid-cols-[auto_1px_1fr] items-center gap-x-6 bg-gray-50 px-5 py-2 rounded">
          <div className="flex items-center gap-3">
            <ToggleSwitch checked={isSTTEnabled} onChange={handleSTTToggle} />
            <span className="text-sm font-medium text-gray-700 w-8">{isSTTEnabled ? 'ON' : 'OFF'}</span>
          </div>

          <div className="bg-gray-300 h-6 w-px"></div>

          <div className={`grid grid-cols-[auto_1fr_1fr] items-center gap-x-4 ${!isSTTEnabled ? 'opacity-50' : ''}`}>
            <span className="text-sm text-gray-600">プロバイダ:</span>
            {sttSettings?.available_providers.map((provider) => (
              <label
                key={provider}
                className="flex items-center gap-2 px-3 py-1 text-sm cursor-pointer transition-colors text-gray-600 hover:text-gray-800"
              >
                <ToggleSwitch
                  checked={selectedSTTProvider === provider}
                  onChange={(checked) => handleSTTProviderToggle(provider, checked)}
                  disabled={!isSTTEnabled}
                  size="small"
                />
                <span className={selectedSTTProvider === provider ? 'font-semibold text-gray-800' : ''}>{STT_PROVIDER_LABELS[provider]}</span>
                <span className="text-xs text-gray-500">- {STT_PROVIDER_DESCRIPTIONS[provider]}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Translation Section */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-gray-800">翻訳API</h3>

        {!isSTTEnabled && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded text-sm">
            翻訳機能を使用するには、字幕機能を有効にしてください。
          </div>
        )}

        {/* Translation Toggle + Provider in grid layout */}
        <div className={`grid grid-cols-[auto_1px_1fr] items-center gap-x-6 bg-gray-50 px-5 py-2 rounded ${!isSTTEnabled ? 'opacity-50' : ''}`}>
          <div className="flex items-center gap-3">
            <ToggleSwitch
              checked={isTranslationEnabled}
              onChange={handleTranslationToggle}
              disabled={!isSTTEnabled}
            />
            <span className="text-sm font-medium text-gray-700 w-8">{isTranslationEnabled ? 'ON' : 'OFF'}</span>
          </div>

          <div className="bg-gray-300 h-6 w-px"></div>

          <div className={`grid grid-cols-[auto_1fr_1fr] items-center gap-x-4 ${!isSTTEnabled ? 'opacity-50' : ''}`}>
            <span className="text-sm text-gray-600">プロバイダ:</span>
            {translationSettings?.available_providers.map((provider) => (
              <label
                key={provider}
                className="flex items-center gap-2 px-3 py-1 text-sm cursor-pointer transition-colors text-gray-600 hover:text-gray-800"
              >
                <ToggleSwitch
                  checked={selectedTranslationProvider === provider}
                  onChange={(checked) => handleTranslationProviderToggle(provider, checked)}
                  disabled={!isSTTEnabled}
                  size="small"
                />
                <span className={selectedTranslationProvider === provider ? 'font-semibold text-gray-800' : ''}>{TRANSLATION_PROVIDER_LABELS[provider]}</span>
                <span className="text-xs text-gray-500">- {TRANSLATION_PROVIDER_DESCRIPTIONS[provider]}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-3 pt-2 pb-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-pink-500 hover:bg-pink-600 text-white font-semibold py-1.5 px-4 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded"
        >
          {isSaving ? '保存中...' : '保存'}
        </button>
        <span className="text-xs text-gray-500">設定をまとめて保存</span>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-5 max-w-xs mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-10 w-10 rounded-full bg-green-100 mb-3">
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 mb-3">設定を保存しました</p>
              <button
                onClick={() => setShowSaveModal(false)}
                className="bg-pink-500 hover:bg-pink-600 text-white font-semibold py-1.5 px-4 text-sm rounded transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
